import { Component, Input, ChangeDetectorRef, Output, EventEmitter, ElementRef } from '@angular/core';
import { CategorizationViewKey } from './add-categorization-modal/categorization-view.types';
import { SpreadingState } from '@components/main/spreading/state/manager';
import { Cell, Column, Row } from '@components/main/spreading/models/models';
import { SourceComment } from '@components/main/spreading/models/source-comment';
import { RoundingService } from '@services/rounding.service';
import { CommentService } from '@services/shared/comment/comment.service';
import { v4 as uuid } from 'uuid';
import {
  readonlyTemplateLabel,
  editableSourceLabel,
  templateCellRoundedDisplay,
  templateOffByRoundedDisplay,
  templateRollupValueRoundedDisplay,
  previousCellRoundedDisplay,
  previousOffByRoundedDisplay,
} from '@components/main/spreading/spreading-display-helpers';
import {
  preparationTypeDisplay,
  reportingIntervalDisplay,
  scenarioDisplay,
} from '@components/main/spreading/spreading-column-header-display';
import {SubmissionRuleActions} from "@services/template-data.service";
import {SpreadingRowLinkService} from '@components/main/spreading/spreading-row-link.service';

@Component({
  selector: 'app-spreading-template-table-view',
  templateUrl: './spreading-template-table-view.component.html',
  styleUrls: ['./spreading-template-table-view.component.scss'],
  standalone: false,
})
export class SpreadingTemplateTableViewComponent {
  readonly reportingIntervalDisplay = reportingIntervalDisplay;
  readonly scenarioDisplay = scenarioDisplay;
  readonly preparationTypeDisplay = preparationTypeDisplay;

  @Input() state: SpreadingState;
  @Input() editingRowLabelIndex = -1;
  @Input() labelEditFocusPane: 'source' | 'template' | null = null;
  @Input() viewInNativeLanguage = true;
  @Input() isDASStatement = false;
  @Input() cellEditFocusPane: 'source' | 'template' | null = null;
  @Input() templateName: string;
  @Input() enableGoalSeek = false;
  @Input() visibleSuggestionsOriginId: number | null = null;
  @Input() visibleSuggestionsOriginColIdx: number | null = null;
  @Input() changeTemplateActionIsVisible: boolean = false;

  @Output() clearedCategorization = new EventEmitter<Row>();
  @Output() goalSeek = new EventEmitter<{ row: Row; colIdx: number }>();
  @Output() hideSuggestions = new EventEmitter<void>();
  @Output() stateChanged = new EventEmitter<SpreadingState>();
  @Output() manualSourceRowInserted = new EventEmitter<Row>();

  @Output() sourceRowLabelChanged = new EventEmitter<{ value: string; row: Row }>();
  @Output() sourceRowLabelEditStart = new EventEmitter<Row>();
  @Output() sourceRowLabelClick = new EventEmitter<Row>();
  @Output() sourceRowCellValueChanged = new EventEmitter<{
    value: string;
    row: Row;
    cellIdx: number;
  }>();
  @Output() sourceRowCellEditStart = new EventEmitter<{ row: Row; cellIdx: number }>();
  @Output() sourceRowCellSelect = new EventEmitter<{ row: Row; cellIdx: number }>();
  @Output() sourceRowCellContextMenu = new EventEmitter<{
    event: MouseEvent;
    row: Row;
    cell: Cell;
    cellIdx: number;
  }>();
  @Output() sourceRowCellValueBeforeEdit = new EventEmitter<string>();
  @Output() sourceRowCellKeyDown = new EventEmitter<{ cell: Cell; event: KeyboardEvent }>();
  @Output() cellBlur = new EventEmitter<void>();
  @Output() previousPeriodCellViewComment = new EventEmitter<{ event: MouseEvent; cell: Cell }>();
  @Output() previousPeriodColumnViewComment = new EventEmitter<{ event: MouseEvent; column: Column }>();
  @Output() sourceRowLabelContextMenu = new EventEmitter<{ event: MouseEvent; row: Row }>();
  @Output() changeTemplateClick = new EventEmitter<void>();

  readonly MANUAL_ITEM_LABEL_PLACEHOLDER = 'Enter item name...';

  expandedRows: Set<string> = new Set();
  allRowsExpanded = false;
  showAddCategorizationModal = false;
  modalRow: Row | null = null;
  modalButtonElement: HTMLElement | null = null;
  modalInitialView: CategorizationViewKey = 'document-item';
  showRecategorizeModal = false;
  recategorizeRow: Row | null = null;
  recategorizeButtonElement: HTMLElement | null = null;

  private readonly rowExpandIds = new Map<Row, string>();

  constructor(
    private cdr: ChangeDetectorRef,
    private roundingService: RoundingService,
    private commentService: CommentService,
    private rowLinkService: SpreadingRowLinkService,
    private el: ElementRef<HTMLElement>
  ) {}

  /**
   * Reveal this table's copy of a categorized document row (triggered by the
   * "View ->" action in that row's categorization/validation tooltip): expand
   * the template items it sits under so it is visible, then scroll to it and
   * flash it green for 3 seconds.
   */
  revealSourceRow(sourceRow: Row): void {
    const rowUuid = sourceRow?.uuid;
    if (!rowUuid || !this.state?.rows) {
      return;
    }
    const ancestors = this._findSourceRowAncestors(this.state.rows, rowUuid, []);
    if (ancestors === null) {
      return;
    }
    if (ancestors.length) {
      const expanded = new Set(this.expandedRows);
      ancestors.forEach((ancestor) => expanded.add(this.getRowId(ancestor)));
      this.expandedRows = expanded;
    }
    this.cdr.detectChanges();
    // Defer so the newly-expanded rows are rendered before we scroll to them.
    setTimeout(() => {
      // Scoped to this table: the Document Details row carries the same key.
      this.rowLinkService.flashRow(
        `[data-row-key="${rowUuid}"]`,
        this.el.nativeElement
      );
    });
  }

  /**
   * Depth-first search for the categorized document row with the given uuid,
   * returning the chain of rows that must be expanded to show it (empty when
   * nothing needs expanding), or `null` when it is not in the tree. A
   * validation row renders alongside the template row it validates, so that
   * template row itself does not need to be expanded.
   */
  private _findSourceRowAncestors(
    rows: Row[],
    rowUuid: string,
    trail: Row[]
  ): Row[] | null {
    for (const row of rows) {
      if (row.uuid === rowUuid || row.validationRow?.uuid === rowUuid) {
        return trail;
      }
      for (const childGroup of [row.children, row.previousOnlyChildren]) {
        if (childGroup && childGroup.length) {
          const found = this._findSourceRowAncestors(childGroup, rowUuid, [
            ...trail,
            row,
          ]);
          if (found) {
            return found;
          }
        }
      }
    }
    return null;
  }

  templateCellDisplay(row: Row, colIdx: number): { rounded: number } | null {
    if (!this.state) {
      return null;
    }
    return templateCellRoundedDisplay(this.state, row, colIdx, this.roundingService);
  }

  templateOffByDisplay(row: Row, colIdx: number): { rounded: number } | null {
    if (!this.state) {
      return null;
    }
    return templateOffByRoundedDisplay(this.state, row, colIdx, this.roundingService);
  }

  templateRollupValueDisplay(row: Row, colIdx: number): { rounded: number } | null {
    if (!this.state) {
      return null;
    }
    return templateRollupValueRoundedDisplay(this.state, row, colIdx, this.roundingService);
  }

  previousCellDisplay(row: Row, pIdx: number): { rounded: number } | null {
    if (!this.state) {
      return null;
    }
    return previousCellRoundedDisplay(this.state, row, pIdx, this.roundingService);
  }

  previousOffByDisplay(row: Row, pIdx: number): { rounded: number } | null {
    if (!this.state) {
      return null;
    }
    return previousOffByRoundedDisplay(this.state, row, pIdx, this.roundingService);
  }

  
  isTemplateLinkedSourceRow(row: Row): boolean {
    if (row.header || row.template === true) {
      return false;
    }
    return this.sourceRowIndex(row) >= 0;
  }

  sourceRowIndex(row: Row): number {
    return this.state?.sourceRows ? this.state.sourceRows.indexOf(row) : -1;
  }
  
  templateTreeReadonlyLabel(row: Row): string {
    return readonlyTemplateLabel(row, this.viewInNativeLanguage);
  }

  labelDisplayValue(row: Row): string {
    return editableSourceLabel(row, this.viewInNativeLanguage);
  }

  isEditingSourceRowLabel(row: Row): boolean {
    const idx = this.sourceRowIndex(row);
    return (
      idx >= 0 &&
      this.editingRowLabelIndex === idx &&
      this.labelEditFocusPane === 'template'
    );
  }

  rowHasTranslation(row: Row): boolean {
    return (
      !!(row.translatedLabel && row.translatedLabel !== row.label) || !!row.labelEditedByUser
    );
  }

  isSourceLabelEditDisabled(row: Row): boolean {
    if (row?.isPreviousOnlyRow) {
      return true;
    }
    // The offset side of an offset pair is derived from its primary (CITI-1274).
    if (this.isDerivedOffsetRow(row)) {
      return true;
    }
    return (
      (!!row?.labelIsLocked && row.itemClass !== 'ManualInputItem') ||
      (!this.viewInNativeLanguage && this.rowHasTranslation(row))
    );
  }

  isDerivedOffsetRow(row: Row): boolean {
    return !!this.state?.isDerivedOffsetRow(row);
  }

  offsetLinkTooltip(row: Row): string {
    const primaryRow = this.state?.getOffsetCounterpart(row);
    const primaryLabel = primaryRow?.label ?? '';
    return `This item offsets the ${primaryLabel} adjustment`;
  }

  /**
   * @param row The row the warning icon belongs to.
   * @param colIdx Index of the row's cell the icon belongs to.
   * @returns Tooltip markup, empty when the cell breaks no rules. Rule messages are
   *          tenant-authored, so each is escaped before reaching the tooltip's HTML.
   */
  submissionRuleTooltip(row: Row, colIdx: number): string {
    const messages = (row.cells?.[colIdx]?.submissionRuleResults ?? [])
      .map(ruleResult => this._escapeHtml(ruleResult.message));
    if (messages.length < 2) {
      return messages[0] ?? '';
    }
    const listItems = messages.map(message => `<li>${message}</li>`).join('');
    return `<ul>${listItems}</ul>`;
  }

  private _escapeHtml(text: string): string {
    return (text || '')
      .split('&').join('&amp;')
      .split('<').join('&lt;')
      .split('>').join('&gt;')
      .split('"').join('&quot;')
      .split("'").join('&#39;');
  }

  isCellEditingDisabledForSourceRow(row: Row): boolean {
    if (row?.isPreviousOnlyRow) {
      return true;
    }
    // The offset side of an offset pair is derived from its primary (CITI-1274).
    if (this.isDerivedOffsetRow(row)) {
      return true;
    }
    if (!this.isDASStatement) {
      return false;
    }
    return (
      ['AdjustedStandardItem', 'NonAdjustableStandardItem'].includes(row?.itemClass) ||
      ['asReported', 'customCalculation'].includes(row?.dynamicAdjustmentStatementItemType)
    );
  }

  cellValueIsNumeric(cell: Cell): boolean {
    return !isNaN(cell.value);
  }

  onStatementCellRerender(): void {
    this.cdr.detectChanges();
  }

  emitSourceLabelChanged(value: string, row: Row): void {
    this.sourceRowLabelChanged.emit({ value, row });
  }

  emitSourceLabelEditStart(row: Row): void {
    this.sourceRowLabelEditStart.emit(row);
  }

  onSourceLineItemLabelClick(row: Row): void {
    this.sourceRowLabelClick.emit(row);
  }

  emitCellValueChanged(value: string, row: Row, cellIdx: number): void {
    this.sourceRowCellValueChanged.emit({ value, row, cellIdx });
  }

  emitCellEditStart(row: Row, cellIdx: number): void {
    this.sourceRowCellEditStart.emit({ row, cellIdx });
  }

  emitCellSelect(row: Row, cellIdx: number): void {
    this.sourceRowCellSelect.emit({ row, cellIdx });
  }

  emitCellContextMenu(
    event: MouseEvent,
    row: Row,
    cell: Cell,
    cellIdx: number
  ): void {
    this.sourceRowCellContextMenu.emit({ event, row, cell, cellIdx });
  }

  emitCellValueBeforeEdit(value: string): void {
    this.sourceRowCellValueBeforeEdit.emit(value);
  }

  emitCellKeyDown(cell: Cell, event: KeyboardEvent): void {
    this.sourceRowCellKeyDown.emit({ cell, event });
  }

  /** True when a previous-period source cell carries a read-only comment. */
  hasPreviousCellComment(row: Row, pIdx: number): boolean {
    const cell = row?.previousCells?.[pIdx];
    return this._sourceCommentsArePopulated(cell?.sourceComments);
  }

  /** True when a previous-period column carries a read-only comment. */
  hasPreviousColumnComment(column: Column): boolean {
    return this._sourceCommentsArePopulated(column?.sourceComments);
  }

  emitPreviousPeriodCellViewComment(event: MouseEvent, row: Row, pIdx: number): void {
    event.stopPropagation();
    const cell = row?.previousCells?.[pIdx];
    if (!cell) {
      return;
    }
    this.previousPeriodCellViewComment.emit({ event, cell });
  }

  emitPreviousPeriodColumnViewComment(event: MouseEvent, column: Column): void {
    event.stopPropagation();
    this.previousPeriodColumnViewComment.emit({ event, column });
  }

  emitSourceRowLabelContextMenu(event: MouseEvent, row: Row): void {
    event.stopPropagation();
    event.preventDefault();
    this.sourceRowLabelContextMenu.emit({ event, row });
  }
  
  emitChangeTemplateClick() {
    this.changeTemplateClick.emit();
  }

  private _sourceCommentsArePopulated(sourceComments: Array<SourceComment> | undefined): boolean {
    return (
      !!sourceComments &&
      sourceComments.length > 0 &&
      this.commentService.commentIsPopulated(sourceComments[0]?.comment)
    );
  }

  getRowId(row: Row): string {
    if (row.uuid) {
      return row.uuid;
    }
    if (row.ref) {
      return row.ref;
    }
    if (row.lineItemId != null) {
      return String(row.lineItemId);
    }
    let id = this.rowExpandIds.get(row);
    if (!id) {
      id = uuid();
      this.rowExpandIds.set(row, id);
    }
    return id;
  }

  isRowExpanded(row: Row): boolean {
    return this.expandedRows.has(this.getRowId(row));
  }

  toggleRowExpansion(row: Row, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const rowId = this.getRowId(row);
    const newExpandedRows = new Set(this.expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    this.expandedRows = newExpandedRows;
    this.cdr.detectChanges();
  }
  
  get expandAllButtonLabel(): string {
    return this.allRowsExpanded ? 'Collapse All' : 'Expand All';
  }

  toggleExpandAll(): void {
    if (this.allRowsExpanded) {
      this.collapseAll();
    } else {
      this.expandAll();
    }
  }

  expandAll(): void {
    const expandableRowIds = new Set<string>();
    this.collectExpandableRowIds(this.state?.rows ?? [], expandableRowIds);
    this.expandedRows = expandableRowIds;
    this.allRowsExpanded = true;
    this.cdr.detectChanges();
  }

  collapseAll(): void {
    this.expandedRows = new Set();
    this.allRowsExpanded = false;
    this.cdr.detectChanges();
  }

  focusFirstChevron(): void {
    const chevron = this.el.nativeElement.querySelector<HTMLElement>(
      '.expand-collapse-icon.grid-nav-cell:not(.disabled)'
    );
    chevron?.focus();
  }

  private collectExpandableRowIds(rows: Row[], acc: Set<string>): void {
    for (const row of rows) {
      if (this.hasExpandableChildren(row)) {
        acc.add(this.getRowId(row));
      }
      if (row.children?.length) {
        this.collectExpandableRowIds(row.children, acc);
      }
      if (row.previousOnlyChildren?.length) {
        this.collectExpandableRowIds(row.previousOnlyChildren, acc);
      }
    }
  }

  expandRow(row: Row) {
    const newExpandedRows = new Set(this.expandedRows);
    const rowId = this.getRowId(row);
    if (!newExpandedRows.has(rowId)) {
      newExpandedRows.add(rowId);
    }
    this.expandedRows = newExpandedRows;
    this.cdr.detectChanges();
  }

  /**
   * True when this row is a prior-period-only row (it has previous-period
   * values but no current spread value) and its current cell at `colIdx`
   * is therefore blank and locked.
   */
  isPreviousOnlyMissingCell(row: Row, colIdx: number): boolean {
    return (
      !row.header &&
      !!row.isPreviousOnlyRow &&
      (!row.cells || row.cells[colIdx] == null)
    );
  }

  /**
   * Count of prior-only cells in the given current-period column across the
   * entire row tree, regardless of whether the rows are currently expanded.
   * The header badge reflects every applicable child cell, including those
   * hidden inside collapsed rows.
   */
  previousOnlyCellCountForColumn(colIdx: number): number {
    if (!this.state?.rows) {
      return 0;
    }
    return this._countPreviousOnly(this.state.rows, colIdx);
  }

  private _countPreviousOnly(rows: Row[], colIdx: number): number {
    let count = 0;
    for (const row of rows) {
      if (this.isPreviousOnlyMissingCell(row, colIdx)) {
        count++;
      }
      if (row.children?.length) {
        count += this._countPreviousOnly(row.children, colIdx);
      }
      if (row.previousOnlyChildren?.length) {
        count += this._countPreviousOnly(row.previousOnlyChildren, colIdx);
      }
    }
    return count;
  }

  /**
   * Reveal every prior-only missing cell in the given current-period column
   * (triggered by the "View" action in the missing-data badge popover):
   * expand the ancestors of each missing cell so they are visible, then scroll
   * the top-most one into view.
   */
  revealPreviousOnlyCells(colIdx: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!this.state?.rows) {
      return;
    }
    const ancestorIds = new Set<string>();
    this._collectPreviousOnlyAncestorIds(this.state.rows, colIdx, [], ancestorIds);
    if (ancestorIds.size) {
      const expanded = new Set(this.expandedRows);
      ancestorIds.forEach((id) => expanded.add(id));
      this.expandedRows = expanded;
    }
    this.cdr.detectChanges();
    // Defer so the newly-expanded rows are rendered before we scroll to them.
    setTimeout(() => {
      this.rowLinkService.scrollRowIntoView(`[data-prev-only-col="${colIdx}"]`);
    });
  }

  /**
   * Depth-first walk collecting the ids of every ancestor row above a prior-only
   * missing cell in the given column, so those ancestors can be expanded.
   */
  private _collectPreviousOnlyAncestorIds(
    rows: Row[],
    colIdx: number,
    trail: Row[],
    acc: Set<string>
  ): void {
    for (const row of rows) {
      if (this.isPreviousOnlyMissingCell(row, colIdx)) {
        trail.forEach((ancestor) => acc.add(this.getRowId(ancestor)));
      }
      for (const childGroup of [row.children, row.previousOnlyChildren]) {
        if (childGroup?.length) {
          this._collectPreviousOnlyAncestorIds(
            childGroup,
            colIdx,
            [...trail, row],
            acc
          );
        }
      }
    }
  }

  hasExpandableChildren(row: Row): boolean {
    if (row.header) return false;
    const hasReal = !!(row.children && row.children.length > 0);
    const hasPriorOnly = !!(row.previousOnlyChildren && row.previousOnlyChildren.length > 0);
    return hasReal || hasPriorOnly;
  }

  expandableChildrenCount(row: Row): number {
    return (row.children?.length || 0) + (row.previousOnlyChildren?.length || 0);
  }

  getLabelIndentLevel(row: Row, parentIndentation: number, isExpandedChild: boolean): number {
    return isExpandedChild
      ? parentIndentation
      : row.indentation !== undefined
        ? row.indentation
        : parentIndentation;
  }

  getValidationRowIndentLevel(row: Row, parentIndentation: number, isExpandedChild: boolean): number {
    return this.getLabelIndentLevel(row, parentIndentation, isExpandedChild) + 1;
  }

  rollupLabelIndentLevel(row: Row, parentIndentation: number, isExpandedChild: boolean): number {
    return this.getLabelIndentLevel(row, parentIndentation, isExpandedChild) + 1;
  }
  
  validationRowLabelIndentLevel(
    row: Row,
    parentIndentation: number,
    isExpandedChild: boolean
  ): number {
    if (row.rollupName) {
      return this.rollupLabelIndentLevel(row, parentIndentation, isExpandedChild) + 1;
    }
    return this.getValidationRowIndentLevel(row, parentIndentation, isExpandedChild);
  }

  rowHasValidationOffBy(row: Row): boolean {
    if (!row.validationRow || !row.cells?.length) {
      return false;
    }
    return row.cells.some(
      (c) =>
        c &&
        c.flag === true &&
        c.offBy != null &&
        c.offBy !== 0
    );
  }
  
  /**
   * True when the given cell is the origin of the currently-visible goal
   * seek session. Keyed on both line item and column so only the clicked
   * cell's button reflects the "hide" state (a row can have flagged cells
   * in many columns).
   */
  isActiveGoalSeekCell(row: Row, colIdx: number): boolean {
    return (
      row.lineItemId === this.visibleSuggestionsOriginId &&
      colIdx === this.visibleSuggestionsOriginColIdx
    );
  }

  onGoalSeekCellClick(row: Row, colIdx: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.isActiveGoalSeekCell(row, colIdx)) {
      this.hideSuggestions.emit();
    } else {
      this.goalSeek.emit({ row, colIdx });
    }
  }

  removeCategorization(row: Row, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.clearedCategorization.emit(row);
  }

  addCategorization(row: Row, event?: Event, initialView: CategorizationViewKey = 'document-item'): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.modalRow = row;
    this.modalButtonElement = event?.target as HTMLElement;
    this.modalInitialView = initialView;
    this.showAddCategorizationModal = true;
    this.cdr.detectChanges();
  }

  onChevronKeydown(row: Row, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.toggleRowExpansion(row, event);
    }
  }

  onAddControlKeydown(row: Row, event: KeyboardEvent): void {
    // Use event.code for Alt combos: on macOS Alt+M / Alt+O change event.key to a special character.
    if (event.altKey && event.code === 'KeyM') {
      this.addCategorization(row, event, 'manual-item');
    } else if (event.altKey && event.code === 'KeyO') {
      this.addCategorization(row, event, 'offset-adjustment');
    } else if (event.key === 'Enter' || event.key === ' ') {
      this.addCategorization(row, event);
    }
  }

  onCellBlur(event: FocusEvent): void {
    const cell = event.currentTarget as HTMLElement | null;
    if (cell?.contains(event.relatedTarget as Node | null)) {
      return;
    }
    this.cellBlur.emit();
  }

  onSourceLabelKeydown(row: Row, event: KeyboardEvent): void {
    // Events from the edit input bubble up; only start editing when the label itself is focused.
    if (event.target !== event.currentTarget) {
      return;
    }
    if ((event.key === 'Enter' || event.key === 'F2') && !this.isSourceLabelEditDisabled(row)) {
      event.preventDefault();
      this.emitSourceLabelEditStart(row);
    }
  }

  onRecategorizeKeydown(row: Row, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.openRecategorizeModal(row, event);
    }
  }

  onRemoveCategorizationKeydown(row: Row, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.removeCategorization(row, event);
    }
  }

  onTemplateCellKeydown(row: Row, colIdx: number, event: KeyboardEvent): void {
    // Events from the edit input bubble up; only start editing when the cell itself is focused.
    if (event.target !== event.currentTarget) {
      this.emitCellKeyDown(row.cells[colIdx], event);
      return;
    }
    if (event.key === 'Enter' || event.key === 'F2') {
      event.preventDefault();
      this.emitCellValueBeforeEdit(row.cells[colIdx].rawText);
      this.emitCellEditStart(row, colIdx);
    }
  }

  onModalStateChanged(newState: SpreadingState): void {
    this.state = newState;
    this.stateChanged.emit(newState);
    this.cdr.detectChanges();
  }

  onManualSourceRowInserted(row: Row): void {
    this.manualSourceRowInserted.emit(row);
  }

  /**
   * Expand the template row an offset adjustment was categorized to, plus its
   * ancestors, so the newly-inserted offset row is immediately visible. The
   * primary side's parent is the clicked (always-visible) modal row and is
   * expanded via the standard close path; this covers the offset side, whose
   * parent template item may live elsewhere in the tree and be collapsed.
   */
  onOffsetAdjustmentInserted(offsetTemplateLineItemId: number): void {
    if (offsetTemplateLineItemId == null || !this.state?.rows) {
      return;
    }
    const chain = this._findRowChainByLineItemId(
      this.state.rows,
      offsetTemplateLineItemId,
      []
    );
    if (!chain?.length) {
      return;
    }
    const expanded = new Set(this.expandedRows);
    chain.forEach((row) => expanded.add(this.getRowId(row)));
    this.expandedRows = expanded;
    this.cdr.detectChanges();
  }

  /**
   * Depth-first search for the row whose `lineItemId` matches, returning the
   * chain of rows from the top level down to and including that row, or `null`
   * when no matching row exists.
   */
  private _findRowChainByLineItemId(
    rows: Row[],
    lineItemId: number,
    trail: Row[]
  ): Row[] | null {
    for (const row of rows) {
      const chain = [...trail, row];
      if (row.lineItemId === lineItemId) {
        return chain;
      }
      for (const childGroup of [row.children, row.previousOnlyChildren]) {
        if (childGroup?.length) {
          const found = this._findRowChainByLineItemId(childGroup, lineItemId, chain);
          if (found) {
            return found;
          }
        }
      }
    }
    return null;
  }

  onModalClose(expandChildren: boolean = false): void {
    if (expandChildren) {
      this.expandRow(this.modalRow);
    }
    this.showAddCategorizationModal = false;
    this.modalRow = null;
    this.modalButtonElement = null;
    this.cdr.detectChanges();
  }

  openRecategorizeModal(row: Row, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.recategorizeRow = row;
    this.recategorizeButtonElement = event?.target as HTMLElement;
    this.showRecategorizeModal = true;
    this.cdr.detectChanges();
  }

  onRecategorizeStateChanged(newState: SpreadingState): void {
    this.state = newState;
    this.stateChanged.emit(newState);
    this.cdr.detectChanges();
  }

  onRecategorizeModalClose(): void {
    this.showRecategorizeModal = false;
    this.recategorizeRow = null;
    this.recategorizeButtonElement = null;
    this.cdr.detectChanges();
  }

  protected readonly SubmissionRuleActions = SubmissionRuleActions;
}
